import * as React from 'react';
import { useCallback, useEffect } from 'react';

import { AiJobStatusResponse } from '~api/ai/types';
import { personalQuizUrl } from '~api/urls';
import { useAtom, useSetAtom } from 'jotai';
import { useDispatch, useSelector } from 'react-redux';

import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';
import { usePollHistory, useStartPoll } from '~shared/polling';
import {
  createNoOpHandler,
  createUploadToS3Handler,
} from '~teacher/components/FileDrop/FileHandlers';
import { FileHandlerConfigBuilder } from '~teacher/components/FileDrop/FileHandlers/FileHandlerConfig';
import { useFileDropConfig } from '~teacher/components/FileDrop/hooks/useFileDropConfig';
import { AIQuestionsLoadingSpinner } from '~teacher/components/QuizEditor/AIQuestionsLoadingSpinner';
import { useImportFromDoc } from '~teacher/hook/ai/useImportFromDoc';
import { useAiErrorHandler } from '~teacher/hook/useAiErrorHandler';
import { workspaceKeys } from '~teacher/hook/workspaces/workspaceQueryKeyFactory';
import { queryClient } from '~teacher/queryClient';
import { loadQuizBySocNumber } from '~teacher/slices/quiz/quizAppThunks';
import { selectEditingQuiz } from '~teacher/slices/quiz/quizSelectors';
import { refreshEditingQuizQuestions } from '~teacher/slices/quiz/quizSlice';
import { proCheck } from '~teacher/util/proCheck';

import {
  extractAddedQuestionSetAtom,
  extractQuestionsPaywallAtom,
  extractQuestionsRequestIdAtom,
  extractQuestionsStepAtom,
} from './atoms';
import { ExtractQuestionsError } from './ExtractQuestionsError';
import { ExtractQuestionsForm } from './ExtractQuestionsForm';
import { ExtractQuestionsHeader } from './ExtractQuestionsHeader';
import { ExtractQuestionsResults } from './ExtractQuestionsResults';
import { useExtractQuestionsReducer } from './useExtractQuestionsReducer';

const MAX_FILES = 1;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

interface ExtractQuestionFromDocProps {
  quizId: number;
  workspaceId: string;
  workspaceQuizId: string;
  insertionOrder: number;
  onClose: () => void;
}

export function ExtractQuestions({
  quizId,
  workspaceId,
  workspaceQuizId,
  insertionOrder,
  onClose,
}: ExtractQuestionFromDocProps) {
  const handleAiError = useAiErrorHandler({
    featureTag: 'ai-extract-questions',
  });

  const {
    state: { includeExplanation },
    dispatch,
  } = useExtractQuestionsReducer();

  const isPro = proCheck.isPro();
  const handler = React.useMemo(() => {
    const onError = () => {
      toast.critical(translate('Required field(s) missing'));
    };

    return isPro ? createUploadToS3Handler({ onError }) : createNoOpHandler();
  }, [isPro]);

  const fileHandlerConfig = React.useMemo(
    () =>
      new FileHandlerConfigBuilder()
        .addHandlerForGroups(handler, [
          { extensions: ['pdf'], mimeTypes: ['application/pdf'] },
          { extensions: ['csv'], mimeTypes: ['text/csv'] },
          { extensions: ['jpg', 'jpeg'], mimeTypes: ['image/jpeg'] },
          { extensions: ['png'], mimeTypes: ['image/png'] },
        ])
        .build(),
    [handler]
  );

  const fileDropConfig = useFileDropConfig({
    maxFileCount: MAX_FILES,
    maxFileSize: MAX_FILE_SIZE,
    ...fileHandlerConfig,
  });

  const quiz = useSelector(selectEditingQuiz);
  const reduxDispatch = useDispatch();
  const [requestId, setRequestId] = useAtom(extractQuestionsRequestIdAtom);
  const [step, setStep] = useAtom(extractQuestionsStepAtom);

  const setPaywallOpen = useSetAtom(extractQuestionsPaywallAtom);
  const { startPoll } = useStartPoll();

  const importFromDocMutation = useImportFromDoc({
    quizId,
    workspaceId,
    workspaceQuizId,
    insertionOrder,
  });

  const refreshQuizData = useCallback(() => {
    if (workspaceId && workspaceQuizId) {
      queryClient
        .invalidateQueries(
          workspaceKeys.byWorkspaceQuizId({
            workspaceQuizId: workspaceQuizId,
            workspaceId: workspaceId,
          })
        )
        .catch((error) => {
          console.error(
            '[ExtractQuestions] Error refreshing workspace quiz:',
            error
          );
        });
    } else if (quiz?.socNumber) {
      reduxDispatch(
        loadQuizBySocNumber({
          socNumber: quiz.socNumber,
          quizUrl: personalQuizUrl,
        })
      )
        .then(() => reduxDispatch(refreshEditingQuizQuestions({ quizId })))
        .catch((error) => {
          console.error(
            '[ExtractQuestions] Error refreshing personal quiz:',
            error
          );
        });
    }
  }, [workspaceId, workspaceQuizId, quiz?.socNumber, reduxDispatch, quizId]);

  const {
    data: pollData,
    isFailed,
    isTimeout,
    cleanup,
  } = usePollHistory<AiJobStatusResponse['data']>({
    requestId,
    onCompleted: () => {
      refreshQuizData();
    },
    cleanupOnUnmount: true,
  });

  const resetAddedQuestions = useSetAtom(extractAddedQuestionSetAtom.resetAtom);

  const handleBack = useCallback(() => {
    cleanup();
    resetAddedQuestions();
    importFromDocMutation.reset();
    setRequestId(null);
    setStep('form');
  }, [
    cleanup,
    resetAddedQuestions,
    importFromDocMutation,
    setRequestId,
    setStep,
  ]);

  // Update step when questions are available
  useEffect(() => {
    if (
      step === 'loading' &&
      pollData?.questions &&
      pollData.questions.length > 0
    ) {
      setStep('results');
    }
    if (step === 'loading' && (isFailed || isTimeout)) {
      setStep('error');
    }
  }, [step, pollData, isFailed, isTimeout, setStep]);

  const handleExtract = () => {
    const fileKey = fileDropConfig.tasks[0]?.result?.fileKey;
    const fileType = fileDropConfig.tasks[0]?.file.type;
    const fileSize = fileDropConfig.tasks[0]?.file.size;

    // Check if user has paid plan
    if (!proCheck.hasPaidPlan()) {
      setPaywallOpen(true);
      return;
    }

    if (!fileKey) {
      toast.critical(translate('Please upload a document first'));
      return;
    }

    // Clean up the previous request to refresh UI
    setRequestId(null);
    cleanup();

    // reset mutation before retry
    importFromDocMutation.reset();
    setStep('loading');

    importFromDocMutation.mutate(
      { fileKey, includeExplanation },
      {
        onSuccess: (data) => {
          // Backend always processes async - start polling & stay on loading step
          if (data?.async && data?.requestId) {
            setRequestId(data.requestId);

            // Pass analytics metadata to poller, which sends after completion
            startPoll({
              id: `extract-${data.requestId}`,
              type: 'ai-extract-questions',
              metadata: {
                requestId: data.requestId,
                quizId,
                workspaceId,
                workspaceQuizId,
                socNumber: quiz?.socNumber,
                analyticsMetadata: {
                  fileUploaded: !!fileKey,
                  fileUploadType: fileType || null,
                  fileUploadSizeBytes: fileSize || null,
                  filesUploadedCount: MAX_FILES,
                  includeExplanations: includeExplanation,
                },
              },
            });
          }
        },
        onError: async (error) => {
          setStep('error');
          await handleAiError(error);
        },
      }
    );
  };

  if (step === 'error' || importFromDocMutation.isError) {
    return <ExtractQuestionsError onRetry={handleExtract} />;
  }

  if (step === 'loading') {
    return (
      <>
        <div className="pb-4">
          <ExtractQuestionsHeader />
        </div>
        <AIQuestionsLoadingSpinner goBack={handleBack} isFileUpload={true} />
      </>
    );
  }

  if (step === 'results' && requestId) {
    return (
      <ExtractQuestionsResults
        pollData={pollData}
        isFailed={isFailed}
        isTimeout={isTimeout}
        cleanup={cleanup}
        onClose={onClose}
        onBack={handleBack}
        refreshQuizData={refreshQuizData}
        handleExtract={handleExtract}
      />
    );
  }

  // Show form
  return (
    <ExtractQuestionsForm
      dispatch={dispatch}
      includeExplanation={includeExplanation}
      onExtract={handleExtract}
      {...fileDropConfig}
    />
  );
}
